import React, { FC } from 'react'
import { Paper } from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { Gateway } from 'src/api'
import { GatewayColumnsDataGrid } from 'src/components/columns/gateway'
import { toInfoColumn } from 'src/components/columns/infoColumn'
import { usePatchGatewayRequest } from 'src/hooks/api'
import { useAddPopupMessage } from 'src/hooks/ui'

const gridSx = {
  border: 'none',
  '& .MuiDataGrid-columnHeaderTitleContainerContent': { paddingLeft: '5px', overflow: 'visible' },
  '& .MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'normal',
    lineHeight: 1,
    fontSize: '13px',
    fontWeight: 400,
    color: 'rgba(0, 0, 0, 0.6)',
    overflow: 'visible'
  }
}

const gatewayColumns: GridColDef<Gateway>[] = [
  // The list column self-links to the gateway's own page (and adds staging/lost labels) — drop it here.
  toInfoColumn(GatewayColumnsDataGrid.DeviceId, { renderCell: undefined }),
  toInfoColumn(GatewayColumnsDataGrid.IMEI),
  toInfoColumn(GatewayColumnsDataGrid.DevEUI),
  toInfoColumn(GatewayColumnsDataGrid.ExternalDeviceId),
  toInfoColumn(GatewayColumnsDataGrid.Name),
  toInfoColumn(GatewayColumnsDataGrid.Contractor),
  toInfoColumn(GatewayColumnsDataGrid.CreatedAt),
  GatewayColumnsDataGrid.Comment
]

interface GatewayInfoProps {
  gateway: Gateway
  refetchGateway?: () => void
}

export const GatewayInfo: FC<GatewayInfoProps> = (props) => {
  const { gateway, refetchGateway } = props
  const addMessage = useAddPopupMessage()
  const updateGatewayRequest = usePatchGatewayRequest()
  const gridMinWidth = gatewayColumns.reduce((sum, column) => sum + (column.minWidth ?? 100), 0)

  const processRowUpdate = async (newRow: Gateway, oldRow: Gateway) => {
    const { comment: newComment } = newRow
    const { comment: oldComment } = oldRow
    const updatedRow: Gateway = await new Promise((resolve) => {
      if (newComment !== oldComment) {
        updateGatewayRequest.mutate(
          {
            gatewayId: newRow.id,
            payload: { comment: newComment }
          },
          {
            onSuccess: () => {
              addMessage({ text: 'The comment updated', type: 'success' })
              if (typeof refetchGateway === 'function') refetchGateway()
              resolve(newRow)
            },
            onError: (error) => {
              addMessage({ text: error?.message, type: 'error' })
              resolve(oldRow)
            }
          }
        )
      }
      return newRow
    })
    return updatedRow
  }

  return (
    <Paper sx={{ width: '100%', overflowX: 'auto' }}>
      <DataGridPremium<Gateway>
        autoHeight
        disableRowSelectionOnClick
        hideFooter
        rows={[gateway]}
        columns={gatewayColumns}
        getRowId={(row) => row.uniqueId}
        rowThreshold={2}
        columnThreshold={2}
        localeText={{ columnMenuSortAsc: 'Sort A-Z', columnMenuSortDesc: 'Sort Z-A' }}
        sx={{ ...gridSx, minWidth: gridMinWidth }}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          if (error) addMessage({ text: error, type: 'error' })
        }}
      />
    </Paper>
  )
}
